import React from "react";
import { Linking, Platform, StyleSheet, View } from "react-native";
import { brand, colors, crisisResources, layout } from "@/constants";
import type { CrisisCooldownBody } from "@/utils/serenity-api-client";
import Card from "./base/Card";
import Icon, { IconName } from "./base/Icon";
import Interactive from "./base/Interactive";
import Modal from "./base/Modal";
import Text from "./base/Text";
import TextButton from "./base/TextButton";

// Resource rows are static info, not tap-to-call buttons. `tel:` and `sms:`
// links work on mobile browsers but trigger Chrome's "open external app"
// prompt on desktop — broken UX for the platform we actually ship on
// (web-only). Mobile users can still tap phone numbers via the browser's
// built-in phone-number detection. The protocol link is a regular https URL
// and stays clickable everywhere.

/**
 * Props for the CrisisResourceModal.
 */
interface CrisisResourceModalProps {
  /** Whether the modal is visible. */
  visible: boolean;
  /**
   * When set, the modal shows pause-specific copy (the resume time and a
   * gentler header). When null, the modal shows the same resources without
   * the pause framing — used by the drawer "Crisis resources" item and the
   * chat footer.
   */
  cooldown?: CrisisCooldownBody | null;
  /** Dismiss callback. The modal is non-dismissable via tap-outside or back
   *  so the user has to engage with the resources or actively close. */
  onClose: () => void;
}

/**
 * Resources block, used by both the pause and the standalone surfaces.
 */
type Resources = CrisisCooldownBody["resources"];

/**
 * Crisis resources modal.
 *
 * Rendered as an overlay (not a full-screen takeover) — the underlying
 * surface stays visible to communicate "this action is blocked" (pause
 * variant) or "here are the resources" (standalone variant). Tap-outside
 * dismissal is disabled by construction.
 */
export default function CrisisResourceModal({
  visible,
  cooldown,
  onClose
}: CrisisResourceModalProps) {
  const resources: Resources = cooldown?.resources ?? crisisResources;
  const resumeLabel = cooldown?.resumeAt
    ? formatTime(new Date(cooldown.resumeAt))
    : null;

  return (
    <Modal
      visible={visible}
      size="medium"
      onRequestClose={onClose}
      dismissOnBackdropPress={false}
      footer={
        <View style={styles.footer}>
          <TextButton
            buttonText="Close"
            variant="secondary"
            onPress={onClose}
            accessibilityLabel="Close"
          />
        </View>
      }
    >
      <View style={styles.header}>
        <Text variant="semiBold" size="large" style={styles.center}>
          {cooldown ? "Let’s pause for a moment" : "Crisis resources"}
        </Text>
        <Text size="medium" color="secondary" style={styles.center}>
          If you or someone you know is in crisis, these resources are available
          24/7.
          {resumeLabel && ` You can start a session at ${resumeLabel}.`}
        </Text>
      </View>

      <View style={styles.resources}>
        <ResourceRow
          icon="support"
          iconColor={colors.primary}
          label={resources.lifeline.label}
          sublabel={`Call or text ${resources.lifeline.phone}`}
        />
        <ResourceRow
          icon="support"
          iconColor={colors.primary}
          label={resources.crisisTextLine.label}
          sublabel={`Text ${resources.crisisTextLine.smsKeyword} to ${resources.crisisTextLine.smsNumber}`}
        />
        <ResourceRow
          icon="emergency"
          iconColor={colors.error}
          label={resources.emergency.label}
          sublabel={`Call ${resources.emergency.phone} if you are in immediate danger`}
        />
      </View>

      <View style={styles.protocolRow}>
        <Interactive
          onPress={() => openLink(resources.protocolUrl)}
          accessibilityLabel={`How ${brand.name} responds to crisis`}
        >
          <Text size="small" color="secondary" style={styles.protocolLink}>
            How {brand.name} responds to crisis
          </Text>
        </Interactive>
      </View>
    </Modal>
  );
}

interface ResourceRowProps {
  icon: IconName;
  iconColor: string;
  label: string;
  sublabel: string;
}

function ResourceRow({ icon, iconColor, label, sublabel }: ResourceRowProps) {
  return (
    <Card backgroundColor={colors.surfaceContainer} style={styles.resourceCard}>
      <Icon name={icon} size="large" style={{ color: iconColor }} />
      <View style={styles.resourceText}>
        <Text size="small" color="secondary">
          {label}
        </Text>
        <Text variant="medium" size="medium">
          {sublabel}
        </Text>
      </View>
    </Card>
  );
}

/**
 * Open an https URL. Used only for the protocol-page link, which is the one
 * actionable link in the modal — phone-number rows are intentionally static.
 */
function openLink(url: string): void {
  if (Platform.OS === "web") {
    if (typeof window !== "undefined") {
      window.location.href = url;
    }
    return;
  }
  Linking.openURL(url);
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit"
  });
}

const styles = StyleSheet.create({
  header: {
    alignItems: "center",
    gap: layout.space.sm,
    marginBottom: layout.space.lg
  },
  center: {
    textAlign: "center"
  },
  resources: {
    gap: layout.space.md
  },
  resourceCard: {
    flexDirection: "row",
    alignItems: "center",
    gap: layout.space.md,
    padding: layout.space.md
  },
  resourceText: {
    flex: 1,
    gap: layout.space.xs
  },
  protocolRow: {
    marginTop: layout.space.lg,
    alignItems: "center"
  },
  protocolLink: {
    textDecorationLine: "underline"
  },
  footer: {
    flex: 1,
    alignItems: "center"
  }
});
